import { useEffect, useState } from "react";
import { useData } from "../contexts/DataContext";
import { fmtPhone } from "../lib/time";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Phone, Mail, MapPin, Loader2, Trash2, Edit } from "lucide-react";
import { CardGridSkeleton } from "../components/skeletons";
import { useAutosave } from "../hooks/useAutosave";
import AutosaveStatus from "../components/AutosaveStatus";

export default function Suppliers() {
  const { 
    suppliers, 
    fetchSuppliers, 
    createSupplier, 
    updateSupplier,
    deleteSupplier,
    dropdownOptions,
    fetchDropdownOptions,
    loading 
  } = useData();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    category: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSuppliers();
    fetchDropdownOptions();
  }, [fetchSuppliers, fetchDropdownOptions]);

  const supplierCategories = dropdownOptions.supplier_categories || [];

  const filteredSuppliers = suppliers.filter(supplier => 
    supplier.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.contact_person?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingSupplier) {
        await updateSupplier(editingSupplier.id, formData);
        toast.success("Supplier updated!");
      } else {
        await createSupplier(formData);
        toast.success("Supplier created!");
      }
      setSubmitting(false);
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save supplier");
      setSubmitting(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData(supplier);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this supplier?")) {
      try {
        await deleteSupplier(id);
        toast.success("Moved to trash");
      } catch (error) {
        toast.error("Failed to delete supplier");
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSupplier(null);
    setFormData({
      name: "",
      contact_person: "",
      phone: "",
      email: "",
      address: "",
      category: "",
      notes: ""
    });
  };

  // Autosave — only for EXISTING supplier rows (not the "Add" flow, where
  // the user hasn't committed to creating yet). Silent, debounced 900ms.
  const { status: autosaveStatus, savedAt, saveNow } = useAutosave(
    formData,
    async (value) => {
      if (!editingSupplier) return;
      await updateSupplier(editingSupplier.id, value);
    },
    { delay: 900, enabled: !!editingSupplier && showModal },
  );

  // Flush any pending edits before the modal closes so nothing is lost.
  const handleModalOpenChange = (open) => {
    if (!open) {
      saveNow();
      closeModal();
    }
  };

  return (
    <div className="min-h-screen">
      <Header title="Suppliers">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6"
          onClick={() => setShowModal(true)}
          data-testid="create-supplier-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Supplier
        </Button>
      </Header>

      <div className="p-4 sm:p-6">
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 max-w-md"
            data-testid="search-suppliers"
          />
        </div>

        {loading ? (
          <CardGridSkeleton cards={6} />
        ) : filteredSuppliers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSuppliers.map((supplier) => (
              <div
                key={supplier.id}
                className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow group"
                data-testid={`supplier-card-${supplier.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-800 text-lg">{supplier.name}</h3>
                    {supplier.category && (
                      <Badge variant="secondary" className="mt-1">
                        {supplierCategories.find(c => c.value === supplier.category)?.label || supplier.category}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(supplier)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(supplier.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {supplier.contact_person && (
                  <p className="text-sm text-slate-600 mb-2">Contact: {supplier.contact_person}</p>
                )}

                <div className="space-y-2 text-sm text-slate-600">
                  {supplier.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{supplier.phone}</span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{supplier.email}</span>
                    </div>
                  )}
                  {supplier.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{supplier.address}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {searchQuery ? "No suppliers found" : "No suppliers yet"}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchQuery ? "Try a different search" : "Add your first supplier"}
            </p>
            {!searchQuery && (
              <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => setShowModal(true)}>
                <Plus className="w-5 h-5 mr-2" />Add Supplier
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={handleModalOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
            <DialogDescription>
              {editingSupplier ? "Changes save automatically." : "Add a new supplier or vendor"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Supplier Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Company Name"
                required
                className="h-12"
                data-testid="supplier-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="John Smith"
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {supplierCategories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: fmtPhone(e.target.value) })}
                  placeholder="(555) 123-4567"
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="supplier@example.com"
                  className="h-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-3 pt-4">
              {editingSupplier && (
                <div className="flex-shrink-0">
                  <AutosaveStatus status={autosaveStatus} savedAt={savedAt} />
                </div>
              )}
              <div className="flex-1" />
              <Button type="button" variant="outline" className="h-12 px-6" onClick={() => handleModalOpenChange(false)}>
                {editingSupplier ? "Done" : "Cancel"}
              </Button>
              {!editingSupplier && (
                <Button type="submit" className="h-12 px-6 bg-[#0F4C81] hover:bg-[#0D4270]" disabled={submitting} data-testid="submit-supplier-btn">
                  {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Saving...</> : "Add Supplier"}
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
